import {
  BoundCheckboxField,
  BoundSelectField,
  Button,
  Css,
  FormLines,
  ModalBody,
  ModalFooter,
  ModalHeader,
  useModal,
} from "@homebound/beam";
import { ObjectConfig, useFormState } from "@homebound/form-state";
import { useCallback } from "react";
import { baseDownloadUrl } from "src/context";
import { Maybe } from "src/generated/graphqlTypes";
import { PageBidPackageDetailFragment } from "src/routes/bids/BidPackageDetailPage.generated";
import { openInSelf } from "src/utils/window";

export function DownloadBidModal({ bidPackage }: { bidPackage: PageBidPackageDetailFragment }) {
  const trades = bidPackage.activeRequests.map((request) => ({
    id: request.id,
    name: request.tradePartner.name,
  }));
  const { closeModal } = useModal();
  const formState = useFormState({
    config: formConfig,
    init: {
      onlyOnce: true,
      input: {
        bidPackageRequestId: trades.first?.id,
        // Default to full bid for historical bids
        fullBid: bidPackage.isHistoricalBid,
      },
    },
  });

  const handleDownload = useCallback(async () => {
    const params = new URLSearchParams({
      type: bidPackage.isUnitBased ? "unitBasedBidPackage" : "planBasedBidPackage",
      bidPackageRequestId: formState.bidPackageRequestId.value!,
      fullBid: formState.fullBid.value ? "true" : "false",
    });
    openInSelf(`${baseDownloadUrl()}/xlsx?${params.toString()}`);
  }, [formState]);

  return (
    <div data-testid="downloadBidModal">
      <ModalHeader>Download Bid</ModalHeader>
      <ModalBody>
        <div css={Css.mb3.$}>Download the bid package as an .xlsx file.</div>
        <FormLines>
          <BoundSelectField field={formState.bidPackageRequestId} label="Trade Partner" options={trades} />
          <BoundCheckboxField field={formState.fullBid} label="Include all lines in the bid package" />
        </FormLines>
      </ModalBody>
      <ModalFooter>
        <>
          <Button label="Cancel" onClick={closeModal} variant="quaternary" />
          <Button label="Download" onClick={handleDownload} />
        </>
      </ModalFooter>
    </div>
  );
}

type DownloadBidFormValue = {
  bidPackageRequestId: Maybe<string>;
  fullBid: Maybe<boolean>;
};
const formConfig: ObjectConfig<DownloadBidFormValue> = {
  bidPackageRequestId: { type: "value" },
  fullBid: { type: "value" },
};
